import {memo, useContext, useEffect, useState} from 'react'

import cx from 'classnames'

import jsCookie from '@s-ui/js/lib/cookie'
import AtomButton, {atomButtonDesigns, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import FormCaptcha from '@s-ui/react-form-captcha'
import MoleculeButtonGroup from '@s-ui/react-molecule-button-group'
import MoleculeCheckboxField from '@s-ui/react-molecule-checkbox-field'
import MoleculeInputField from '@s-ui/react-molecule-input-field'
import MoleculeTextareaField, {MoleculeTextareaResizes} from '@s-ui/react-molecule-textarea-field'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {filterBasicTypes} from '@adv-mt/mt-filter-basic'
import FilterWrapperBasic, {filterWrapperBasicTypes} from '@adv-mt/mt-filter-wrapper-basic'
import GigyaLoader from '@adv-mt/mt-gigya-loader'
import {useCaptcha} from '@adv-mt/mt-hook-use-captcha'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import BlockedUserToast from '@adv-mt/mt-notifications-blocked-user-toast'
import IconCheckboxEmpty from '@adv-mt/mt-svg-icons/lib/CheckboxEmpty.js'
import IconCheckboxFilled from '@adv-mt/mt-svg-icons/lib/CheckboxFilled.js'

import {useChecksAndValidations} from '../../hooks/useChecksAndValidations.js'
import useContactSellerFormContext from '../../hooks/useContactSellerFormContext.js'
import {FIELDS} from '../../useContactSellerState/reducer.js'
import {BLOCKED_USERS_COOKIE_KEY, getLeadEnrichmentFromLocalStorage} from '../../utils.js'
import ContactButtons from '../ContactButtons/index.js'
import ContactSellerConfirmation from '../ContactSellerConfirmation/contact-seller-confirmation.js'

const MAX_CHARS_MESSAGE_FIELD = 4000
const GENERIC_ERROR = 'GENERIC_ERROR'

const filterName = 'leadEnrichment'

const FormContactSellerAdDetailPage = () => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const LEAD_ENRICHMENT_ENABLED = config.get('LEAD_ENRICHMENT_ENABLED')

  const {user, isLogged} = useContext(AuthenticationContext)

  const [hasCreatedAlert, setHasCreatedAlert] = useState(false)
  const [leadEnrichmentValue, setLeadEnrichmentValue] = useState([])
  const [buyerIntentions, setBuyerIntentions] = useState([])

  const formContactSellerId = config.get('FORM_CONTACT_SELLER_ID')

  const {HAS_ALERTS_ON_LEAD_ENABLED} = config.get('features')
  const {LAST_CONTACT_DETAIL_KEY} = config.get('contact')
  const {TARGET_BUYER_IDS} = config.get('subscription')
  const {DATA_PROTECTION_PATH_PATTERN, USER_ALERTS_MATCHES_PAGE_NAME, USER_FAVORITE_ADS_PAGE_NAME} = config.get('page')
  const linkDataProtection = DATA_PROTECTION_PATH_PATTERN

  const {state, actions, currentProps} = useContactSellerFormContext()

  const {
    ad,
    blockedUsers,
    initialMessage,
    isModal,
    isZipCodeEnabled,
    onContact,
    pageName,
    showTargetBuyerOptions,
    targetBuyer,
    onCreateAlert,
    enableAlertCreation,
    enableLeadEnrichment,
    enableAuthBeforeLead,
    initialLeadEnrichmentValue,
    sellerId,
    textareaSize
  } = currentProps

  const showLeadEnrichment = LEAD_ENRICHMENT_ENABLED && enableLeadEnrichment && ad?.isProfessional
  const isAlertsOrFavoritesPage = [USER_ALERTS_MATCHES_PAGE_NAME, USER_FAVORITE_ADS_PAGE_NAME].includes(pageName)

  const isDesktop = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: true
  })

  // Fetch buyer intentions from backend when lead enrichment is enabled
  useEffect(() => {
    if (!showLeadEnrichment) return

    const fetchBuyerIntentions = async () => {
      const [error, response] = await domain.get('get_buyer_intentions_user_use_case').execute()
      if (!error && response?.buyerIntentions) {
        setBuyerIntentions(response.buyerIntentions)
      }
    }
    fetchBuyerIntentions()
  }, [domain, showLeadEnrichment])

  // Map buyer intentions to filter items
  const ITEMS_LEAD_ENRICHMENT = buyerIntentions.map(intention => ({
    id: intention.id,
    label: intention.shortLabel
  }))

  const {checkValidField, showFormFieldLabel, showTermsAndConditions} = useChecksAndValidations({
    isUseZipCode: isZipCodeEnabled
  })

  const [showUnblockedToast, setShowUnblockToast] = useState(state.isSellerBlocked && isModal)

  // Creating the alert needs a userId, hence the logged-in check
  const canCreateAlert = enableAlertCreation && isLogged && HAS_ALERTS_ON_LEAD_ENABLED

  const shouldCreateAlert =
    canCreateAlert && !hasCreatedAlert && state.hasContacted && state[FIELDS.ENABLE_ALERTS.NAME].value

  if (shouldCreateAlert) {
    onCreateAlert()
    setHasCreatedAlert(true)
  }

  const {
    setCurrentUser,
    setFormWithCaptcha,
    updateField,
    hasSuccesfulCaptcha,
    setFieldsLastContact,
    setIsSellerBlocked
  } = actions

  const {isActive: isCaptchaActive, siteKey} = useCaptcha()

  const getLastContactDetails = async () => {
    const [, lastContactDetails] = await domain.get('get_last_contact_details_seller_use_case').execute()
    if (lastContactDetails) {
      setFieldsLastContact({
        [FIELDS.EMAIL.NAME]: lastContactDetails.email,
        ...(initialMessage
          ? {[FIELDS.MESSAGE.NAME]: initialMessage}
          : {[FIELDS.MESSAGE.NAME]: lastContactDetails.message}),
        [FIELDS.NAME.NAME]: lastContactDetails.name,
        [FIELDS.PHONE.NAME]: lastContactDetails.phone || '',
        [FIELDS.ZIPCODE.NAME]: lastContactDetails.zipCode,
        date: lastContactDetails.date
      })
    }
  }

  const handleBlockSellerToast = isToastVisible => {
    setShowUnblockToast(isToastVisible)
  }

  const handleUnblockAndSubmitContactButton = async userToUnblockId => {
    const [error] = await domain.get('unblock_user_use_case').execute({userToUnblockId: sellerId})
    if (error) return GENERIC_ERROR
    setIsSellerBlocked(false)
    const currentBlockedUsers = blockedUsers.filter(item => item !== sellerId)
    jsCookie.set(BLOCKED_USERS_COOKIE_KEY, JSON.stringify(currentBlockedUsers))
  }

  useEffect(() => {
    if (state.hasContacted && onContact) {
      onContact({
        isFinancingLead: state.isFinancingLead || leadEnrichmentValue.includes('FINANCING'),
        message: state[FIELDS.MESSAGE.NAME].value || i18n.t('FORM.CONTACT_MESSAGE_PLACEHOLDER'),
        userHasBeenRegistered: state.userHasBeenRegistered,
        autoGeneratedPassword: state.autoGeneratedPassword,
        messageContactId: state.messageContactId
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onContact, state.hasContacted])

  useEffect(() => {
    user
      ? setCurrentUser({
          [FIELDS.EMAIL.NAME]: user.email,
          [FIELDS.NAME.NAME]: user.name,
          [FIELDS.PHONE.NAME]: user.phone || '',
          [FIELDS.ZIPCODE.NAME]: user.zipCode,
          ...(initialMessage && {[FIELDS.MESSAGE.NAME]: initialMessage})
        })
      : getLastContactDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (isCaptchaActive) {
      setFormWithCaptcha()
    }
  }, [isCaptchaActive]) // eslint-disable-line

  useEffect(() => {
    const leadEnrichmentValue = getLeadEnrichmentFromLocalStorage({
      localStorage,
      key: LAST_CONTACT_DETAIL_KEY
    })
    if (leadEnrichmentValue) {
      setLeadEnrichmentValue(leadEnrichmentValue)
    }
  }, [LAST_CONTACT_DETAIL_KEY])

  useEffect(() => {
    if (initialLeadEnrichmentValue?.length) {
      setLeadEnrichmentValue(initialLeadEnrichmentValue)
    }
  }, [initialLeadEnrichmentValue])

  const handleChange = ({field, value}) => {
    updateField(field, value)
  }

  const handleOnCaptchaSubmit = token => {
    Boolean(token) && hasSuccesfulCaptcha(token)
  }

  const {hasBeenSubmitted} = state
  const linkConditions = i18n.t('URL_COCHES_CONDITIONS')
  const labels = {
    message: i18n.t(FIELDS.MESSAGE.LABEL),
    name: i18n.t(FIELDS.NAME.LABEL),
    namePlaceholder: i18n.t(FIELDS.NAME.PLACEHOLDER),
    email: i18n.t(FIELDS.EMAIL.LABEL),
    emailPlaceholder: i18n.t(FIELDS.EMAIL.PLACEHOLDER),
    phone: i18n.t(FIELDS.PHONE.LABEL),
    phonePlaceholder: i18n.t(FIELDS.PHONE.PLACEHOLDER),
    zipCode: i18n.t(FIELDS.ZIPCODE.PLACEHOLDER),
    terms_and_conditions: i18n.t('FORM.LEGAL_CHECKBOX_LABEL', {
      linkStartConditions: `<a href="${linkConditions}" class='mt-FormContactSeller-link' target='_blank'>`,
      linkEndConditions: '</a>',
      linkStartPrivacy: `<a href="${linkDataProtection}" class='mt-FormContactSeller-link' target='_blank'>`,
      linkEndPrivacy: '</a>'
    }),
    lastCallContactOnMailContact: i18n.t('FORM.LEGAL_LAST_CALL_CHECKBOX_LABEL', {
      linkStartConditions: `<a href="${linkConditions}" class='mt-FormContactSeller-link' target='_blank'>`,
      linkEndConditions: '</a>',
      linkStartPrivacy: `<a href="${linkDataProtection}" class='mt-FormContactSeller-link' target='_blank'>`,
      linkEndPrivacy: '</a>'
    }),
    platePlaceholder: i18n.t(FIELDS.PLATE.PLACEHOLDER),
    enableAlerts: i18n.t('FORM.ENABLE_ALERTS_CHECKBOX')
  }

  const labelElementTermsAndConditions = (
    <label
      className="mt-FormContactSeller-checkboxLabel"
      htmlFor={FIELDS.TERMS_AND_CONDITIONS.NAME}
      dangerouslySetInnerHTML={{__html: labels.terms_and_conditions}}
    />
  )
  const enableAlertsID = 'enableAlerts'
  const enableAlertsLabel = (
    <label
      className="mt-FormContactSeller-checkboxLabel"
      htmlFor={enableAlertsID}
      dangerouslySetInnerHTML={{__html: labels.enableAlerts}}
    />
  )

  const rentingTargetBuyers = [
    {id: TARGET_BUYER_IDS.PRIVATES, label: i18n.t('FORM.TARGET_BUYER_PRIVATES')},
    {id: TARGET_BUYER_IDS.BUSINESS, label: i18n.t('FORM.TARGET_BUYER_BUSINESS')}
  ]

  const getErrorTextByField = field => {
    const fieldHasErrors = !checkValidField(field.NAME)
    return hasBeenSubmitted && fieldHasErrors && i18n.t(field.ERROR)
  }

  const formContactSellerContainerClass = cx('mt-FormContactSeller', {
    'is-modal': isModal
  })

  const contactButtonsClass = 'mt-FormContactSeller-contactButton'

  const getDescriptionFieldLabel = () => (showFormFieldLabel && isDesktop ? labels.message : null)

  return (
    <>
      <div id={formContactSellerId} className={formContactSellerContainerClass}>
        <BlockedUserToast
          isToastVisible={showUnblockedToast}
          handleUnblockAndSubmitContactButton={handleUnblockAndSubmitContactButton}
          notificationStatus={state.isSellerBlocked ? 'system' : 'success'}
          onCloseToast={handleBlockSellerToast}
          pageName={pageName}
        />

        {state.hasContacted && <ContactSellerConfirmation pageName={pageName} />}
        {!state.hasContacted && (
          <>
            {showLeadEnrichment && (
              <div className="mt-FormContactSeller-formField">
                {/* Temporary until the rollout of the feature */}
                {!isAlertsOrFavoritesPage && (
                  <span style={{fontWeight: 'bold', marginBottom: '8px', display: 'block'}}>
                    {i18n.t('FORM.CONTACT_SELLER_LABEL')}
                  </span>
                )}
                <FilterWrapperBasic
                  filters={[
                    {
                      name: 'leadEnrichment',
                      options: ITEMS_LEAD_ENRICHMENT,
                      type: filterBasicTypes.MULTI_SELECTION_ROUNDED_OUTLINED
                    }
                  ]}
                  group={'leadEnrichmentGroup'}
                  legend="Lead Enrichment"
                  onChange={({value}) => setLeadEnrichmentValue(value)}
                  value={{[filterName]: leadEnrichmentValue}}
                  wrapper={filterWrapperBasicTypes.NONE}
                />
              </div>
            )}
            <form aria-label="contact form" className="mt-FormContactSeller-form">
              <div className="mt-FormContactSeller-formField">
                <MoleculeTextareaField
                  autoHideHelpText
                  id={labels.message}
                  label={getDescriptionFieldLabel()}
                  maxChars={MAX_CHARS_MESSAGE_FIELD}
                  size={textareaSize}
                  onChange={(e, {value}) => handleChange({field: FIELDS.MESSAGE.NAME, value})}
                  placeholder={i18n.t('FORM.CONTACT_MESSAGE_PLACEHOLDER')}
                  resize={MoleculeTextareaResizes.VERTICAL}
                  tabIndex="0"
                  textCharacters={i18n.t('COMMON.PLURALS.CHARACTER', MAX_CHARS_MESSAGE_FIELD).toLowerCase()}
                  value={state[FIELDS.MESSAGE.NAME].value}
                />
              </div>

              <div className="mt-FormContactSeller-formField">
                <MoleculeInputField
                  id={labels.name}
                  placeholder={labels.namePlaceholder}
                  value={state[FIELDS.NAME.NAME].value}
                  onChange={(e, {value}) => handleChange({field: FIELDS.NAME.NAME, value})}
                  errorText={getErrorTextByField(FIELDS.NAME)}
                  disabled={state[FIELDS.NAME.NAME].isDisabled}
                  tabIndex="0"
                />
              </div>
              <div className="mt-FormContactSeller-formField">
                <MoleculeInputField
                  type="email"
                  id={labels.email}
                  placeholder={labels.emailPlaceholder}
                  value={state[FIELDS.EMAIL.NAME].value}
                  onChange={(e, {value}) => handleChange({field: FIELDS.EMAIL.NAME, value})}
                  errorText={getErrorTextByField(FIELDS.EMAIL)}
                  disabled={state[FIELDS.EMAIL.NAME].isDisabled}
                  tabIndex="0"
                />
              </div>
              <div className="mt-FormContactSeller-formField">
                <MoleculeInputField
                  type="tel"
                  id={labels.phone}
                  placeholder={labels.phonePlaceholder}
                  value={state[FIELDS.PHONE.NAME].value}
                  onChange={(e, {value}) => handleChange({field: FIELDS.PHONE.NAME, value: value.replace(/\D/g, '')})}
                  errorText={getErrorTextByField(FIELDS.PHONE)}
                  disabled={state[FIELDS.PHONE.NAME].isDisabled}
                  tabIndex="0"
                  maxLength={9}
                />
              </div>
              {isZipCodeEnabled && (
                <div className="mt-FormContactSeller-formField">
                  <MoleculeInputField
                    type="numeric"
                    id={labels.zipCode}
                    placeholder={labels.zipCode}
                    value={state[FIELDS.ZIPCODE.NAME].value}
                    onChange={(e, {value}) => handleChange({field: FIELDS.ZIPCODE.NAME, value})}
                    errorText={getErrorTextByField(FIELDS.ZIPCODE)}
                    disabled={state[FIELDS.ZIPCODE.NAME].isDisabled}
                    tabIndex="0"
                  />
                </div>
              )}
              {showTargetBuyerOptions && (
                <div className="mt-FormContactSeller-formField">
                  <MoleculeButtonGroup
                    fullWidth
                    size={atomButtonSizes.MEDIUM}
                    display="grid"
                    onClick={e => e.preventDefault()}
                  >
                    {rentingTargetBuyers.map(({id, label}) => (
                      <AtomButton
                        design={atomButtonDesigns.OUTLINE}
                        focused={id === targetBuyer}
                        key={id}
                        id={id}
                        onClick={() => {
                          handleChange({field: FIELDS.TARGET_BUYER.NAME, value: id})
                        }}
                        value={id}
                      >
                        {label}
                      </AtomButton>
                    ))}
                  </MoleculeButtonGroup>
                </div>
              )}
              {showTermsAndConditions && (
                <div className="mt-FormContactSeller-formField">
                  <MoleculeCheckboxField
                    checked={state[FIELDS.TERMS_AND_CONDITIONS.NAME].value}
                    checkedIcon={IconCheckboxFilled}
                    uncheckedIcon={IconCheckboxEmpty}
                    id={FIELDS.TERMS_AND_CONDITIONS.NAME}
                    onChange={(e, {checked}) =>
                      handleChange({
                        field: FIELDS.TERMS_AND_CONDITIONS.NAME,
                        value: checked
                      })
                    }
                    nodeLabel={labelElementTermsAndConditions}
                    errorText={getErrorTextByField(FIELDS.TERMS_AND_CONDITIONS)}
                    disabled={state[FIELDS.TERMS_AND_CONDITIONS.NAME].isDisabled}
                    aria-label={labels.terms_and_conditions}
                  />
                </div>
              )}
              {canCreateAlert && (
                <div className="mt-FormContactSeller-formField">
                  <MoleculeCheckboxField
                    checked={state[FIELDS.ENABLE_ALERTS.NAME].value}
                    checkedIcon={IconCheckboxFilled}
                    uncheckedIcon={IconCheckboxEmpty}
                    id={enableAlertsID}
                    onChange={(e, {checked}) =>
                      handleChange({
                        field: FIELDS.ENABLE_ALERTS.NAME,
                        value: checked
                      })
                    }
                    nodeLabel={enableAlertsLabel}
                    aria-label={labels.enableAlerts}
                  />
                </div>
              )}
              {state.hasGenericError && (
                <span className="mt-FormContactSeller-genericError">{i18n.t('COMMON.SOMETHING_WENT_WRONG')}</span>
              )}
              {state.hasCaptcha && (
                <div className="mt-FormContactSeller-captcha">
                  <FormCaptcha locale={i18n.locale} onSubmit={handleOnCaptchaSubmit} siteKey={siteKey} />
                </div>
              )}
              <div className={contactButtonsClass}>
                <div className="mt-FormContactSeller-submit">
                  <ContactButtons
                    leadEnrichmentValue={leadEnrichmentValue}
                    handleBlockSellerToast={handleBlockSellerToast}
                    isContactDisabled={showUnblockedToast}
                    showLeadEnrichment={showLeadEnrichment}
                    enableAuthBeforeLead={enableAuthBeforeLead}
                  />
                </div>
              </div>
            </form>
          </>
        )}
      </div>
      <GigyaLoader render={() => null} timeoutRender={() => null} />
    </>
  )
}

FormContactSellerAdDetailPage.displayName = 'FormContactSellerAdDetailPage'

const MemoFormContactSellerAdDetailPage = memo(FormContactSellerAdDetailPage)
MemoFormContactSellerAdDetailPage.displayName = 'FormContactSellerAdDetailPage'

export default MemoFormContactSellerAdDetailPage
